import AttachFileIcon from '@mui/icons-material/AttachFile';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { MessagingChannelReplyMode } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';

import AttachButtonHint from '~components/ComposerAttachments/AttachButtonHint';
import ComposerAttachments from '~components/ComposerAttachments/ComposerAttachments';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { MESSAGING_ATTACHMENT_CONFIG } from '~hooks/useComposerAttachments/composerAttachmentConfigs';
import useAttachmentPaste from '~hooks/useComposerAttachments/useAttachmentPaste';
import useComposerAttachments from '~hooks/useComposerAttachments/useComposerAttachments';
import { MessagingChannel } from '~hooks/useMessaging/models';
import useReplyPrivately from '~hooks/useMessaging/useReplyPrivately';
import { MESSAGE_CONTROL_HEIGHT_IN_PX } from '~pages/Messages/components/MessageInputShell';
import MessageSendRow from '~pages/Messages/components/MessageSendRow';
import { useMessagingAttachmentDropTarget } from '~pages/Messages/components/MessagingAttachmentDropZone';
import { BROADCAST_ACCENT_WIDTH_IN_PX } from '~pages/Messages/messagingBroadcastAccent';
import { useMessagingBroadcastReply } from '~pages/Messages/MessagingBroadcastReplyContext';
import { messagingBroadcastTitle } from '~pages/Messages/messagingChannelLabels';
import {
  ComposerUnavailableReason,
  composerUnavailableReason,
  isChannelOrganizer,
} from '~pages/Messages/messagingComposerPolicy';
import { isMessageTooLong } from '~pages/Messages/messagingContentLimit';
import { useMessagingDraftContent } from '~pages/Messages/MessagingDraftContext';
import { useMessagingOutbox } from '~pages/Messages/MessagingOutboxContext';
import { useStores } from '~store';

const UNAVAILABLE_NOTE_KEYS: Record<ComposerUnavailableReason, string> = {
  archived: 'messaging.conversation_archived',
  replies_off: 'messaging.replies_off',
};

type MessageComposerProps = {
  focusOnMount?: boolean;
  channel: MessagingChannel;
  draftKey?: string;
  onChannelResolved?: (channelId: string) => void;
  onTyping?: () => void;
};

const MessageComposer = observer(
  ({
    channel,
    draftKey = channel.id,
    focusOnMount = false,
    onChannelResolved,
    onTyping,
  }: MessageComposerProps) => {
    const theme = useTheme();
    const { userStore } = useStores();
    const { sendThroughOutbox } = useMessagingOutbox();
    const { broadcastReplyByChannelId, clearBroadcastReply, setBroadcastReply } =
      useMessagingBroadcastReply();
    const { replyPrivately } = useReplyPrivately();
    const { changeContent, clearContent, content } = useMessagingDraftContent(draftKey);
    const {
      addFiles,
      attachments,
      clearAttachments,
      clearError: clearAttachmentError,
      blocksSend: attachmentsBlockSend,
      error: attachmentError,
      isAtAttachmentLimit,
      removeAttachment,
    } = useComposerAttachments(MESSAGING_ATTACHMENT_CONFIG);

    const inputRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isOpeningPrivateReply, setIsOpeningPrivateReply] = useState(false);

    const broadcastReply = broadcastReplyByChannelId[channel.id];
    const unavailableReason = composerUnavailableReason(channel, userStore.user?.id);

    useMessagingAttachmentDropTarget(unavailableReason ? null : addFiles);

    useEffect(() => {
      if (focusOnMount) {
        inputRef.current?.focus();
      }
    }, [focusOnMount]);

    const sendableAttachments = attachments.flatMap((attachment) =>
      attachment.status === 'uploaded' && attachment.signedId
        ? [
            {
              filename: attachment.filename,
              isImage: attachment.isImage,
              signedId: attachment.signedId,
            },
          ]
        : [],
    );
    // A message may be files alone, but never files that are still on their way up. An
    // unresolved attachment error also blocks the send, so a refused selection cannot go out
    // as a message the sender believes carries their files.
    const canSend =
      !attachmentsBlockSend &&
      !isMessageTooLong(content) &&
      (content.trim().length > 0 || sendableAttachments.length > 0);

    const send = useCallback(async () => {
      // Guard here rather than on the button alone: Enter reaches this directly, and a send
      // mid-upload would post without the file still on its way and then discard it.
      if (!canSend) return;

      const draft = content.trim();

      // Emptying first is what stops a double send: the second attempt finds no draft.
      clearContent();
      clearAttachments();
      // The outbox entry carries the association from here, including through a retry.
      clearBroadcastReply(channel.id);
      inputRef.current?.focus();

      const resolvedChannelId = await sendThroughOutbox({
        attachments: sendableAttachments,
        channel,
        content: draft,
        repliedToBroadcastChannelId: broadcastReply?.broadcastChannelId,
      });
      if (resolvedChannelId) {
        // A send routed out of a broadcast lands in a channel that may have its own banner
        // parked from an earlier Reply privately, which this send has now answered.
        clearBroadcastReply(resolvedChannelId);
        onChannelResolved?.(resolvedChannelId);
      }
    }, [
      broadcastReply?.broadcastChannelId,
      canSend,
      channel,
      clearAttachments,
      clearBroadcastReply,
      clearContent,
      content,
      onChannelResolved,
      sendableAttachments,
      sendThroughOutbox,
    ]);

    const handleContentChange = useCallback(
      (nextContent: string) => {
        changeContent(nextContent);
        clearAttachmentError();

        if (nextContent.trim().length > 0) {
          onTyping?.();
        }
      },
      [changeContent, clearAttachmentError, onTyping],
    );

    const handleAttachClick = useCallback(() => {
      fileInputRef.current?.click();
    }, []);

    const handleFileInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files ?? []);

        if (selectedFiles.length > 0) {
          addFiles(selectedFiles);
        }

        // Reset so selecting the same file again still fires a change event.
        event.target.value = '';
      },
      [addFiles],
    );

    const handlePaste = useAttachmentPaste(addFiles);

    const openPrivateReply = useCallback(async () => {
      setIsOpeningPrivateReply(true);

      try {
        const privateChannel = await replyPrivately(channel);
        setBroadcastReply(privateChannel.id, {
          broadcastChannelId: channel.id,
          title: messagingBroadcastTitle(channel.name),
        });
        onChannelResolved?.(privateChannel.id);
      } catch {
        // Reported and toasted by the hook; the typed draft is left alone.
      } finally {
        setIsOpeningPrivateReply(false);
      }
    }, [channel, onChannelResolved, replyPrivately, setBroadcastReply]);

    if (unavailableReason) {
      return (
        <Box
          data-test-id="messaging-composer-unavailable"
          sx={{
            alignItems: 'center',
            backgroundColor: theme.semantic.surface,
            borderTop: `1px solid ${theme.semantic.divider}`,
            // Content-box so the note fills the same band as the composer's padded control row.
            boxSizing: 'content-box',
            display: 'flex',
            flexShrink: 0,
            justifyContent: 'center',
            minHeight: MESSAGE_CONTROL_HEIGHT_IN_PX,
            p: 2,
          }}
        >
          <Typography sx={{ color: theme.semantic.textSecondary }} variant="caption">
            {t(UNAVAILABLE_NOTE_KEYS[unavailableReason])}
          </Typography>
        </Box>
      );
    }

    const repliesPrivately =
      channel.replyMode === MessagingChannelReplyMode.REPLY_TO_SENDER &&
      !isChannelOrganizer(channel, userStore.user?.id);

    return (
      <Box
        data-test-id="messaging-composer"
        onPaste={handlePaste}
        sx={{
          backgroundColor: theme.semantic.surface,
          borderTop: `1px solid ${theme.semantic.divider}`,
          flexShrink: 0,
          p: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {repliesPrivately && (
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, px: 0.5 }}>
              <Typography color="text.secondary" variant="caption">
                {t('messaging.reply_goes_privately')}
              </Typography>

              <SmallButton
                color="secondary"
                disabled={isOpeningPrivateReply}
                onClick={() => void openPrivateReply()}
                variant="outlined"
              >
                {t('messaging.reply_privately')}
              </SmallButton>
            </Box>
          )}

          {broadcastReply && (
            <Box
              data-test-id="messaging-broadcast-reply-banner"
              sx={{
                alignItems: 'center',
                backgroundColor: theme.semantic.surfaceSunken,
                borderLeft: `${BROADCAST_ACCENT_WIDTH_IN_PX}px solid ${theme.brandV2.colors.treadOrangeDark}`,
                borderRadius: theme.brandV2.borderRadius,
                display: 'flex',
                gap: 1,
                pl: 1,
                pr: 0.5,
                py: 0.5,
              }}
            >
              <CampaignOutlinedIcon
                sx={{ color: theme.semantic.textSecondary, fontSize: 16 }}
              />

              <Typography
                noWrap
                sx={{ color: theme.semantic.textSecondary, flex: 1 }}
                variant="caption"
              >
                {t('messaging.replying_privately_to', { name: broadcastReply.title })}
              </Typography>

              <IconButton
                aria-label={t('messaging.dismiss_broadcast_reply')}
                onClick={() => clearBroadcastReply(channel.id)}
                size="small"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}

          <ComposerAttachments attachments={attachments} onRemove={removeAttachment} />

          {attachmentError && (
            <Typography sx={{ color: theme.semantic.errorText, px: 0.5 }} variant="body2">
              {attachmentError}
            </Typography>
          )}

          <MessageSendRow
            canSend={canSend}
            content={content}
            inputRef={inputRef}
            leadingControl={
              <>
                <input
                  accept={MESSAGING_ATTACHMENT_CONFIG.accept}
                  data-test-id="messaging-file-input"
                  hidden
                  multiple
                  onChange={handleFileInputChange}
                  ref={fileInputRef}
                  type="file"
                />

                <AttachButtonHint
                  attachmentCount={attachments.length}
                  config={MESSAGING_ATTACHMENT_CONFIG}
                >
                  {(hintId) => (
                    <IconButton
                      aria-describedby={hintId}
                      aria-label={t('attachments.attach_file')}
                      disabled={isAtAttachmentLimit}
                      onClick={handleAttachClick}
                      sx={{
                        backgroundColor: theme.semantic.surfaceSunken,
                        border: `1px solid ${theme.semantic.borderStrong}`,
                        borderRadius: theme.brandV2.borderRadius,
                        color: theme.semantic.textSecondary,
                        flex: `0 0 ${MESSAGE_CONTROL_HEIGHT_IN_PX}px`,
                        height: `${MESSAGE_CONTROL_HEIGHT_IN_PX}px`,
                        width: `${MESSAGE_CONTROL_HEIGHT_IN_PX}px`,
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </AttachButtonHint>
              </>
            }
            onContentChange={handleContentChange}
            onSend={() => void send()}
          />
        </Box>
      </Box>
    );
  },
);

export default MessageComposer;
